// pages/eventos/admin/ListarEventosPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../axiosConfig";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import * as htmlToImage from "html-to-image";
import download from "downloadjs";
import {
  CalendarDays,
  Edit,
  ExternalLink,
  Gift,
  Loader2,
  Plus,
  QrCode,
  Ticket,
  Users,
} from "lucide-react";

import "./ListarEventosPage.css";

const ListarEventosPage = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [statusAlterando, setStatusAlterando] = useState(null);

  const navigate = useNavigate();

  const baseUrl = window?.location?.origin || "https://www.tikpontos.com.br";

  const carregarEventos = () => {
    setLoading(true);

    axios
      .get("/admin/eventos")
      .then((res) => setEventos(Array.isArray(res.data) ? res.data : []))
      .catch(() => setErro("Erro ao carregar eventos."))
      .finally(() => setLoading(false));
  };

  const handleToggleStatus = async (eventoId) => {
    setStatusAlterando(eventoId);

    try {
      await axios.patch(`/admin/eventos/${eventoId}/status`);
      carregarEventos();
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
    } finally {
      setStatusAlterando(null);
    }
  };

  const handleDownloadQRCode = async (slug) => {
    const node = document.getElementById(`qrcode-${slug}`);

    if (!node) return;

    try {
      const dataUrl = await htmlToImage.toPng(node);
      download(dataUrl, `evento-${slug}-qrcode.png`);
    } catch (error) {
      console.error("Erro ao gerar imagem do QR Code:", error);
    }
  };

  useEffect(() => {
    carregarEventos();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const agora = new Date();

    eventos.forEach(async (evento) => {
      if (evento.ativo && new Date(evento.data_fim) < agora) {
        await handleToggleStatus(evento.id);
      }
    });
  }, [eventos]);

  const resumo = useMemo(() => {
    const total = eventos.length;
    const ativos = eventos.filter((evento) => evento.ativo).length;
    const inativos = total - ativos;

    const participantes = eventos.reduce(
      (acc, evento) => acc + Number(evento.limite_participantes || 0),
      0
    );

    return {
      total,
      ativos,
      inativos,
      participantes,
    };
  }, [eventos]);

  const formatarData = (data) => {
    if (!data) return "?";

    try {
      return new Date(data).toLocaleDateString("pt-BR");
    } catch {
      return "?";
    }
  };

  if (loading) {
    return (
      <div className="listarEventosAdmin-state">
        <Loader2 size={26} />
        <p>Carregando eventos...</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="listarEventosAdmin-state erro">
        <p>{erro}</p>
      </div>
    );
  }

  return (
    <div className="listarEventosAdmin-page">
      <div className="listarEventosAdmin-topbar">
        <div>
          <span className="listarEventosAdmin-kicker">Eventos promocionais</span>

          <h2>Eventos criados</h2>

          <p>
            Gerencie eventos presenciais, acompanhe status, acesse sorteios e
            baixe QR Codes para divulgação.
          </p>
        </div>

        <Link to="/admin/eventos/criar" className="listarEventosAdmin-btn primary">
          <Plus size={16} />
          Criar novo evento
        </Link>
      </div>

      <div className="listarEventosAdmin-cards">
        <div className="listarEventosAdmin-card">
          <span>Total de eventos</span>
          <strong>{resumo.total.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="listarEventosAdmin-card success">
          <span>Ativos</span>
          <strong>{resumo.ativos.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="listarEventosAdmin-card muted">
          <span>Inativos</span>
          <strong>{resumo.inativos.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="listarEventosAdmin-card value">
          <span>Limite de participantes</span>
          <strong>{resumo.participantes.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="listarEventosAdmin-tableCard">
        <div className="listarEventosAdmin-tableHeader">
          <div>
            <h3>Lista de eventos</h3>
            <span>{eventos.length.toLocaleString("pt-BR")} evento(s)</span>
          </div>

          <Ticket size={18} />
        </div>

        <div className="listarEventosAdmin-tableWrapper">
          <table className="listarEventosAdmin-table">
            <thead>
              <tr>
                <th>Evento</th>
                <th>Período / Participantes</th>
                <th>Status</th>
                <th>Link público</th>
                <th>QR Code</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {eventos.length === 0 ? (
                <tr>
                  <td colSpan="6" className="listarEventosAdmin-empty">
                    Nenhum evento encontrado.
                  </td>
                </tr>
              ) : (
                eventos.map((evento) => {
                  const eventoUrl = `${baseUrl}/eventos/${evento.slug}`;

                  return (
                    <tr key={evento.id}>
                      <td>
                        <div className="listarEventosAdmin-eventoCell">
                          <div className="listarEventosAdmin-eventoIcon">
                            <Gift size={16} />
                          </div>

                          <div>
                            <strong>{evento.nome_evento || "-"}</strong>
                            <span>{evento.slug}</span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="listarEventosAdmin-infoStack">
                          <span>
                            <CalendarDays size={13} />
                            {formatarData(evento.data_inicio)} até{" "}
                            {formatarData(evento.data_fim)}
                          </span>

                          <span>
                            <Users size={13} />
                            {evento.limite_participantes || "-"} participantes
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="listarEventosAdmin-statusBox">
                          <span
                            className={`listarEventosAdmin-status ${
                              evento.ativo ? "ativo" : "inativo"
                            }`}
                          >
                            {evento.ativo ? "Ativo" : "Inativo"}
                          </span>

                          <label className="listarEventosAdmin-toggle">
                            <input
                              type="checkbox"
                              checked={Boolean(evento.ativo)}
                              disabled={statusAlterando === evento.id}
                              onChange={() => handleToggleStatus(evento.id)}
                            />
                            <span />
                          </label>
                        </div>
                      </td>

                      <td>
                        <div className="listarEventosAdmin-urlBox">
                          <a href={eventoUrl} target="_blank" rel="noreferrer">
                            <ExternalLink size={13} />
                            Abrir evento
                          </a>

                          <small>{eventoUrl}</small>
                        </div>
                      </td>

                      <td>
                        <div className="listarEventosAdmin-qrBox">
                          <div
                            className="listarEventosAdmin-qrContainer"
                            id={`qrcode-${evento.slug}`}
                          >
                            <QRCodeCanvas
                              value={eventoUrl}
                              size={72}
                              bgColor="#ffffff"
                            />
                          </div>

                          <button
                            type="button"
                            className="listarEventosAdmin-btn tiny"
                            onClick={() => handleDownloadQRCode(evento.slug)}
                          >
                            <QrCode size={13} />
                            Baixar
                          </button>
                        </div>
                      </td>

                      <td>
                        <div className="listarEventosAdmin-actions">
                          <button
                            type="button"
                            className="listarEventosAdmin-actionBtn primary"
                            onClick={() =>
                              navigate(`/admin/sorteios?slug=${evento.slug}`)
                            }
                          >
                            <Ticket size={14} />
                            Sorteios
                          </button>

                          <button
                            type="button"
                            className="listarEventosAdmin-actionBtn secondary"
                            onClick={() =>
                              navigate(`/admin/eventos/editar/${evento.slug}`)
                            }
                          >
                            <Edit size={14} />
                            Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ListarEventosPage;